import { ReactNode } from 'react'
import clsx from 'clsx'
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'

type Tone = 'teal' | 'clay' | 'mint' | 'info' | 'warning'

interface StatTileProps {
  title: string
  value: ReactNode
  icon: React.ElementType
  tone?: Tone
  subValue?: ReactNode
  isLoading?: boolean
  isError?: boolean
  onClick?: () => void
  hint?: string
  /** Tabular figures for numeric primary value (keeps digits aligned) */
  mono?: boolean
}

/**
 * Icon tint per tone. The accent tone carries the brand; the rest are
 * deliberately quiet so a dashboard of eight tiles doesn't turn into a
 * colour chart. `highlight` (clay) is rationed to one tile at most.
 */
const TONE: Record<Tone, string> = {
  teal: 'bg-accent-soft text-accent',
  clay: 'bg-highlight-soft text-highlight',
  mint: 'bg-success-soft text-success',
  info: 'bg-surface-alt text-ink-muted',
  warning: 'bg-warning-soft text-warning',
}

/**
 * A dashboard KPI tile with first-class loading and error affordances.
 *
 * Distinct from Card/StatCard: never falls back to a bare "0" when data
 * loading fails — a subtle warning tile makes broken telemetry visible
 * to operators instead of misreading it as a zero metric.
 */
export function StatTile({
  title,
  value,
  icon: Icon,
  tone = 'teal',
  subValue,
  isLoading = false,
  isError = false,
  onClick,
  hint,
  mono = false,
}: StatTileProps) {
  const clickable = !!onClick && !isLoading && !isError

  // Flat surface + hairline. Depth is reserved for overlays, not cards.
  const base =
    'group relative overflow-hidden bg-surface rounded-card border border-hairline p-6 text-left w-full transition-colors duration-fast ease-standard'

  const interactive = clickable
    ? 'hover:border-accent/40 hover:bg-surface-alt/40 focus-visible:outline-none focus-visible:shadow-focus'
    : ''

  if (isLoading) {
    return (
      <div className={clsx(base, 'animate-pulse')}>
        <div className="flex flex-col items-start gap-4">
          <div className="h-12 w-12 rounded-chip bg-surface-alt" />
          <div className="flex-1 space-y-2">
            <div className="h-3 w-24 rounded-chip bg-surface-alt" />
            <div className="h-7 w-20 rounded-chip bg-surface-alt" />
            <div className="h-3 w-32 rounded-chip bg-surface-alt" />
          </div>
        </div>
      </div>
    )
  }

  if (isError) {
    return (
      <div className={clsx(base, 'border-warning/30 bg-warning-soft/40')} role="alert">
        <div className="flex flex-col items-start gap-4">
          <div className="p-3 rounded-chip bg-warning-soft text-warning shrink-0">
            <ExclamationTriangleIcon className="h-6 w-6" />
          </div>
          <div className="min-w-0">
            <p className="font-inter text-body-sm text-ink-muted">{title}</p>
            <p className="font-outfit text-h4 text-warning mt-0.5">Unavailable</p>
            <p className="font-inter text-caption text-ink-faint mt-1">
              Refresh to try again
            </p>
          </div>
        </div>
      </div>
    )
  }

  const inner = (
    <div className="flex flex-col items-start gap-4">
      <div className={clsx('p-2 rounded-xl shrink-0', TONE[tone])}>
        <Icon className="h-6 w-6" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-inter text-body-sm text-ink-muted">{title}</p>
        {/* The number is the hero on a finance dashboard. */}
        <p
          className={clsx(
            'text-[clamp(1.5rem,2.3vw,2rem)] leading-tight text-ink mt-2 break-words tabular',
            mono ? 'font-mono' : 'font-outfit',
          )}
          title={typeof value === 'string' ? value : undefined}
        >
          {value}
        </p>
        {subValue && (
          <p className="font-inter text-caption text-ink-faint mt-2">
            {subValue}
          </p>
        )}
        {hint && <p className="font-inter text-caption text-ink-muted mt-2">{hint}</p>}
      </div>
    </div>
  )

  if (clickable) {
    return (
      <button type="button" onClick={onClick} className={clsx(base, interactive)}>
        {inner}
      </button>
    )
  }

  return <div className={base}>{inner}</div>
}

export default StatTile
